import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Download,
  FileText,
  Info,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { PageLoader } from '../../components/ui/PageLoader';
import { ProgressBar } from '../../components/ui/ProgressBar';
import { PagePicker } from '../../components/features/PagePicker';
import { FileStatusBadge } from '../../components/features/FileStatusBadge';
import { PageStatusTile } from '../../components/features/PageStatusTile';
import {
  downloadExport,
  generateExport,
  selectPages,
} from '../../api/files';
import { triggerDownload } from '../../api/pdfTools';
import { useFileStatus } from '../../hooks/useFileStatus';
import { useAuthStore } from '../../store/auth.store';
import { extractErrorMessage } from '../../api/client';
import { storage } from '../../lib/storage';
import { percent } from '../../lib/format';
import { cn } from '../../lib/cn';
import type { ExportFormat, FilePage } from '../../api/types';

const FORMATS: Array<{ value: ExportFormat; label: string; description: string }> = [
  { value: 'docx', label: 'DOCX', description: 'Word, для редактирования' },
  { value: 'xlsx', label: 'XLSX', description: 'Excel, для таблиц' },
];

export function FileDetailsPage() {
  const { fileGUID = '' } = useParams();
  const navigate = useNavigate();
  const userGUID = useAuthStore((s) => s.user?.guid);
  const queryClient = useQueryClient();

  const [generating, setGenerating] = useState<ExportFormat | null>(null);

  // Один источник правды для статуса. Хук внутри сам поллит во время
  // INIT/IN_PROGRESS, останавливается на COMPLETED/FAILED/SELECT_PAGES.
  const statusQuery = useFileStatus(fileGUID);
  const status = statusQuery.data?.status;
  const details = statusQuery.data;

  // На переходе в финальный статус — обновляем историю файлов,
  // чтобы FilesPage / Dashboard показали актуальный статус.
  const lastStatusRef = useRef<typeof status>();
  useEffect(() => {
    if (!status) return;
    if (
      lastStatusRef.current !== status &&
      (status === 'COMPLETED' || status === 'FAILED')
    ) {
      queryClient.invalidateQueries({ queryKey: ['history', userGUID] });
      queryClient.invalidateQueries({ queryKey: ['tariff', userGUID] });
    }
    lastStatusRef.current = status;
  }, [status, queryClient, userGUID]);

  // Число страниц оригинала для PDF на этапе SELECT_PAGES — сохранили при upload.
  const originalPageCount = storage.getPendingSelectPageCount(fileGUID);

  const selectMutation = useMutation({
    mutationFn: async (pages: number[]) => {
      if (!userGUID) throw new Error('No user');
      await selectPages(userGUID, fileGUID, pages);
    },
    onSuccess: () => {
      toast.success('Страницы отправлены на обработку');
      storage.clearPendingSelectPageCount(fileGUID);
      queryClient.invalidateQueries({ queryKey: ['file-status', userGUID, fileGUID] });
    },
    onError: (error) => toast.error(extractErrorMessage(error)),
  });

  const handleGenerate = async (format: ExportFormat) => {
    if (!userGUID) return;
    try {
      setGenerating(format);
      const gen = await generateExport(userGUID, fileGUID, format, 0);
      const blob = await downloadExport(userGUID, gen.id, format);
      const base = details?.alias?.replace(/\.[^.]+$/, '') ?? 'result';
      triggerDownload(blob, `${base}.${format}`);
      toast.success(`${format.toUpperCase()} готов`);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Не удалось подготовить файл'));
    } finally {
      setGenerating(null);
    }
  };

  if (statusQuery.isLoading) return <PageLoader />;

  if (statusQuery.isError || !details) {
    return (
      <div className="flex flex-col gap-3">
        <ErrorBanner message="Не удалось загрузить информацию о файле." />
        <Button variant="secondary" leftIcon={<ArrowLeft size={16} />} onClick={() => navigate('/files')}>
          К истории
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <Header
        alias={details.alias}
        dateAdded={details.dateAdded}
        pagesSelect={details.pagesSelect}
        status={status ?? 'INIT'}
        onBack={() => navigate('/files')}
      />

      {status === 'SELECT_PAGES' && (
        <Card className="animate-enter">
          <div className="mb-3 flex items-center gap-2">
            <Sparkles size={18} className="text-brand-primary" />
            <h2 className="text-lg font-semibold">Выберите страницы</h2>
          </div>
          <p className="mb-4 text-sm text-text-muted">
            За один раз можно отправить до 10 страниц. По умолчанию выбраны первые.
          </p>
          {originalPageCount ? (
            <PagePicker
              pdfGuid={details.id}
              totalPages={originalPageCount}
              loading={selectMutation.isPending}
              onConfirm={(pages) => selectMutation.mutate(pages)}
            />
          ) : (
            <ErrorBanner
              message={
                <>
                  Не удалось получить список страниц документа (потеряно при
                  перезагрузке). Загрузите файл заново —{' '}
                  <button
                    type="button"
                    className="underline underline-offset-2"
                    onClick={() => navigate('/dashboard')}
                  >
                    на дашборд
                  </button>
                  .
                </>
              }
            />
          )}
        </Card>
      )}

      {(status === 'INIT' || status === 'IN_PROGRESS') && (
        <ProcessingView pages={details.pages} pdfGuid={details.id} />
      )}

      {status === 'COMPLETED' && (
        <CompletedView
          pages={details.pages}
          pdfGuid={details.id}
          generating={generating}
          onGenerate={handleGenerate}
        />
      )}

      {status === 'FAILED' && (
        <Card className="animate-enter flex flex-col gap-3">
          <div className="flex items-center gap-2 text-error">
            <AlertTriangle size={18} />
            <span className="font-medium">Обработка не удалась</span>
          </div>
          <p className="text-sm text-text-muted">
            Попробуйте загрузить файл ещё раз. Если ошибка повторяется — убедитесь,
            что PDF не зашифрован и читается.
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              leftIcon={<RefreshCw size={16} />}
              onClick={() => navigate('/dashboard')}
            >
              Загрузить файл заново
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

// --- Header ----------------------------------------------------------------

interface HeaderProps {
  alias: string;
  dateAdded: string;
  pagesSelect: number;
  status: string;
  onBack: () => void;
}

function Header({ alias, dateAdded, pagesSelect, status, onBack }: HeaderProps) {
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex w-fit items-center gap-1 rounded-full px-2 py-1 text-sm text-text-muted hover:bg-bg-soft hover:text-text"
      >
        <ArrowLeft size={14} />
        Все файлы
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <FileText size={22} className="shrink-0 text-brand-primary" />
            <span className="truncate">{alias}</span>
          </h1>
          <p className="mt-1 text-sm text-text-muted">
            {dateAdded}
            {pagesSelect ? ` · ${pagesSelect} стр. в работе` : ''}
          </p>
        </div>
        <FileStatusBadge status={status as 'INIT'} />
      </div>
    </div>
  );
}

// --- ProcessingView --------------------------------------------------------

interface ProcessingViewProps {
  pages: FilePage[];
  pdfGuid: string;
}

function ProcessingView({ pages, pdfGuid }: ProcessingViewProps) {
  const stats = useMemo(() => {
    const total = pages.length;
    const done = pages.filter((p) => p.status === 'COMPLETED').length;
    const failed = pages.filter((p) => p.status === 'FAILED').length;
    const inProgress = pages.filter((p) => p.status === 'IN_PROGRESS').length;
    return { total, done, failed, inProgress, percent: percent(done, total) };
  }, [pages]);

  // Подзаголовок зависит от под-стейта.
  const subtitle =
    stats.total === 0
      ? 'Готовимся запустить распознавание…'
      : stats.done === stats.total
        ? 'Завершаем…'
        : stats.inProgress > 0
          ? `Сейчас в работе ${stats.inProgress} стр.`
          : 'Поставлено в очередь';

  return (
    <div className="flex flex-col gap-4 animate-enter">
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative flex h-12 w-12 shrink-0 items-center justify-center">
              <div className="absolute inset-0 rounded-full bg-brand-gradient opacity-90" />
              <div
                className="absolute inset-0 rounded-full bg-brand-gradient blur-md opacity-50 animate-pulse-soft"
                aria-hidden
              />
              <Sparkles size={20} className="relative text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold tracking-tight">
                Распознавание идёт
              </p>
              <p className="text-sm text-text-muted">{subtitle}</p>
            </div>
          </div>

          {stats.total > 0 && (
            <div className="text-right">
              <div className="text-3xl font-semibold tabular-nums leading-none text-text">
                {stats.percent}
                <span className="text-base font-medium text-text-muted">%</span>
              </div>
              <div className="mt-0.5 text-xs tabular-nums text-text-muted">
                {stats.done} из {stats.total}
              </div>
            </div>
          )}
        </div>

        <div className="mt-4">
          <ProgressBar value={stats.total === 0 ? 0 : stats.percent} />
        </div>
      </Card>

      {/* Подсказка — Apple-style: spacious, иконкой, без агрессии */}
      <div className="flex items-start gap-2 rounded-[14px] bg-bg-soft px-3.5 py-2.5 text-xs text-text-muted">
        <Info size={14} className="mt-0.5 shrink-0 text-brand-primary" />
        <span>
          Распознавание идёт на сервере. Можно закрыть вкладку — статус
          восстановится, когда вы вернётесь.
        </span>
      </div>

      {pages.length > 0 && (
        <section>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
              Страницы
            </h2>
            {(stats.failed > 0 || stats.inProgress > 0) && (
              <div className="flex items-center gap-2 text-xs text-text-muted">
                {stats.inProgress > 0 && (
                  <Badge variant="warning">{stats.inProgress} в работе</Badge>
                )}
                {stats.failed > 0 && (
                  <Badge variant="error">{stats.failed} с ошибкой</Badge>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
            {pages.map((p) => (
              <PageStatusTile key={p.id} page={p} pdfGuid={pdfGuid} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// --- CompletedView ---------------------------------------------------------

interface CompletedViewProps {
  pages: FilePage[];
  pdfGuid: string;
  generating: ExportFormat | null;
  onGenerate: (format: ExportFormat) => void;
}

function CompletedView({ pages, pdfGuid, generating, onGenerate }: CompletedViewProps) {
  const total = pages.length;
  const succeeded = pages.filter((p) => p.status === 'COMPLETED').length;
  const partial = succeeded < total;

  return (
    <div className="flex flex-col gap-4 animate-enter">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              Готово к скачиванию
            </h2>
            <p className="mt-1 text-sm text-text-muted">
              Распознано страниц: {succeeded} из {total}.
              {partial && ' Часть страниц пропущена из-за ошибок.'}
            </p>
          </div>
          {!partial && (
            <Badge variant="success">100% распознано</Badge>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {FORMATS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => onGenerate(f.value)}
              disabled={generating !== null}
              className={cn(
                'group flex items-center justify-between gap-3 rounded-[14px] border border-stroke bg-white px-4 py-3 text-left',
                'transition-all duration-200 ease-[var(--ease-out-soft)]',
                'hover:-translate-y-0.5 hover:border-brand-primary hover:shadow-soft',
                'active:scale-[0.98]',
                'disabled:pointer-events-none disabled:opacity-50',
              )}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold">{f.label}</p>
                <p className="text-xs text-text-muted">{f.description}</p>
              </div>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-gradient text-white shadow-soft transition-transform duration-200 group-hover:scale-110">
                {generating === f.value ? (
                  <span className="block h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                ) : (
                  <Download size={16} />
                )}
              </span>
            </button>
          ))}
        </div>

        <p className="mt-3 text-xs text-text-muted">
          Файл со всеми распознанными страницами склеит сервер — это занимает
          несколько секунд.
        </p>
      </Card>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-text-muted">
          Страницы
        </h2>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
          {pages.map((p) => (
            <PageStatusTile key={p.id} page={p} pdfGuid={pdfGuid} />
          ))}
        </div>
      </section>
    </div>
  );
}

